import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit, FileText, Trash2 } from 'lucide-react';
import { Pagination } from '@/components/shared/Pagination';

const StatusBadge = ({ statut }) => {
  const styles = {
    actif: 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200',
    reserve: 'bg-amber-100 text-amber-700',
    vendu: 'bg-blue-100 text-blue-700',
    mort: 'bg-gray-100 text-gray-700',
  };
  return (
    <span className={`px-2 py-1 lg:px-3 lg:py-1 rounded-full text-xs font-medium ${styles[statut] || styles.mort}`}>
      {statut === 'reserve' ? 'réservé' : statut}
    </span>
  );
};

export const SheepTableView = ({ sheep: paginatedSheep, allSheep, breeds, BACKEND_URL, calculateAge, getAgeDetails, canWrite, canDelete, onEdit, onHealth, onDelete, currentPage, totalPages, startIndex, itemsPerPage, totalItems, onPageChange }) => (
  <Card className="border-0 shadow-md">
    <CardHeader><CardTitle className="text-xl">Tableau des moutons</CardTitle></CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-gray-200 dark:border-gray-800">
              <th className="text-left py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm">Photo</th>
              <th className="text-left py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm">Numéro</th>
              <th className="text-left py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm hidden md:table-cell">Nom</th>
              <th className="text-left py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm">Race</th>
              <th className="text-center py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm hidden lg:table-cell">Sexe</th>
              <th className="text-center py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm hidden lg:table-cell">Âge</th>
              <th className="text-center py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm">Statut</th>
              <th className="text-right py-3 px-2 lg:px-4 font-semibold text-gray-700 text-xs lg:text-sm">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedSheep.length === 0 ? (
              <tr><td colSpan="8" className="text-center py-8 text-gray-500 dark:text-gray-400">Aucun mouton enregistré</td></tr>
            ) : (
              paginatedSheep.map((s) => {
                const breed = breeds.find(b => b.nom === s.race);
                const mere = s.mere_id ? allSheep.find(m => m.id === s.mere_id) : null;
                return (
                  <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50 dark:hover:bg-gray-900">
                    <td className="py-3 px-2 lg:px-4">
                      {s.photo_url ? (
                        <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg overflow-hidden">
                          <img src={`${BACKEND_URL}${s.photo_url}`} alt={`Mouton ${s.numero}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                        </div>
                      ) : (
                        <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                          <span className="text-slate-700 dark:text-slate-300 text-sm lg:text-lg">🐑</span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-2 lg:px-4 font-semibold text-gray-900 text-xs lg:text-sm">
                      N° {s.numero}
                      {s.origine === 'naissance' && <span className="ml-1 text-slate-600 dark:text-slate-400" title="Né(e) à la ferme">🐣</span>}
                    </td>
                    <td className="py-3 px-2 lg:px-4 text-gray-900 text-xs lg:text-sm hidden md:table-cell">
                      <div>
                        {s.nom || '-'}
                        {mere && <div className="text-xs text-pink-600 mt-0.5">Mère: N° {mere.numero}</div>}
                      </div>
                    </td>
                    <td className="py-3 px-2 lg:px-4 text-xs lg:text-sm">
                      {breed ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: breed.couleur }}></div>
                          <span className="text-gray-900 dark:text-white">{s.race}</span>
                        </div>
                      ) : <span className="text-gray-600 dark:text-gray-400">{s.race}</span>}
                    </td>
                    <td className="py-3 px-2 lg:px-4 text-center hidden lg:table-cell">
                      <span className={`px-2 py-1 rounded text-xs ${s.sexe === 'male' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                        {s.sexe === 'male' ? '♂' : '♀'}
                      </span>
                    </td>
                    <td className="py-3 px-2 lg:px-4 text-center text-gray-600 text-xs lg:text-sm hidden lg:table-cell">
                      <span title={getAgeDetails(s.date_naissance, s.statut === 'mort' ? s.date_deces : null).tooltip} className="cursor-help">
                        {calculateAge(s.date_naissance, s.statut === 'mort' ? s.date_deces : null)}
                      </span>
                    </td>
                    <td className="py-3 px-2 lg:px-4 text-center"><StatusBadge statut={s.statut} /></td>
                    <td className="py-3 px-1 lg:px-4 text-right">
                      <div className="flex gap-0.5 lg:gap-1 justify-end">
                        {canWrite && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => onEdit(s)} className="text-blue-600 hover:bg-blue-50 p-1 lg:p-2" title="Modifier"><Edit className="w-3 h-3 lg:w-4 lg:h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => onHealth(s)} className="text-green-600 hover:bg-green-50 p-1 lg:p-2" title="Santé"><FileText className="w-3 h-3 lg:w-4 lg:h-4" /></Button>
                          </>
                        )}
                        {canDelete && <Button variant="ghost" size="sm" onClick={() => onDelete(s.id)} className="text-red-600 hover:bg-red-50 p-1 lg:p-2" title="Supprimer"><Trash2 className="w-3 h-3 lg:w-4 lg:h-4" /></Button>}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} startIndex={startIndex} itemsPerPage={itemsPerPage} totalItems={totalItems} label="moutons" />
    </CardContent>
  </Card>
);

export const SheepGridView = ({ sheep: paginatedSheep, allSheep, breeds, BACKEND_URL, calculateAge, canWrite, canDelete, onEdit, onHealth, onDelete, currentPage, totalPages, onPageChange }) => (
  <Card className="border-0 shadow-md">
    <CardHeader><CardTitle className="text-xl">Cartes des moutons</CardTitle></CardHeader>
    <CardContent>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paginatedSheep.map((s) => {
          const mere = s.mere_id ? allSheep.find(m => m.id === s.mere_id) : null;
          return (
            <Card key={s.id} className="card-hover border-gray-200" data-testid={`sheep-card-${s.numero}`}>
              {s.photo_url ? (
                <div className="relative h-48 w-full overflow-hidden rounded-t-lg">
                  <img src={`${BACKEND_URL}${s.photo_url}`} alt={`Mouton ${s.numero}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              ) : (
                <div className="h-48 w-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center rounded-t-lg">
                  <span className="text-slate-700 dark:text-slate-300 text-6xl">🐑</span>
                </div>
              )}
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="text-lg">N° {s.numero}</span>
                  <StatusBadge statut={s.statut} />
                </CardTitle>
                {s.nom && <p className="text-gray-600 font-medium">{s.nom}</p>}
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-500 dark:text-gray-400">Race:</span><p className="font-medium">{s.race}</p></div>
                  <div><span className="text-gray-500 dark:text-gray-400">Sexe:</span><p className="font-medium">{s.sexe === 'male' ? 'Mâle' : 'Femelle'}</p></div>
                  <div><span className="text-gray-500 dark:text-gray-400">Âge:</span><p className="font-medium">{calculateAge(s.date_naissance, s.statut === 'mort' ? s.date_deces : null)}</p></div>
                  {s.poids && <div><span className="text-gray-500 dark:text-gray-400">Poids:</span><p className="font-medium">{s.poids} kg</p></div>}
                </div>
                {mere && (
                  <div className="bg-pink-50 dark:bg-pink-950 rounded-lg p-2 text-sm">
                    <span className="text-pink-600 dark:text-pink-400">Mère: </span>
                    <span className="font-medium">N° {mere.numero}{mere.nom ? ` - ${mere.nom}` : ''}</span>
                  </div>
                )}
                {s.origine === 'naissance' && <span className="inline-block bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 text-xs px-2 py-1 rounded-full">Né(e) à la ferme</span>}
                {canWrite && (
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => onEdit(s)} className="flex-1"><Edit className="w-4 h-4 mr-1" />Modifier</Button>
                    <Button variant="outline" size="sm" onClick={() => onHealth(s)} className="flex-1"><FileText className="w-4 h-4 mr-1" />Santé</Button>
                    {canDelete && <Button variant="outline" size="sm" onClick={() => onDelete(s.id)} className="text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} startIndex={0} itemsPerPage={paginatedSheep.length} totalItems={paginatedSheep.length} label="moutons" />
    </CardContent>
  </Card>
);
